import axios from "axios";
import { getToken } from "../services/storageService";
import { resolveGatewayUpstreamHttpUrl } from "../runtime/gatewayEnv";

const normalizeHttpBase = (raw: string) =>
  raw.startsWith("http") ? raw : `https://${raw}`;

const getAuthBaseUrl = () => {
  const base = resolveGatewayUpstreamHttpUrl();
  return base ? normalizeHttpBase(base) : "";
};

export const authApiClient = axios.create({
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

export const stagingApiClient = axios.create({
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

const attachGatewayAndBearer = (config: any) => {
  config.baseURL = getAuthBaseUrl();
  config.headers = config.headers ?? {};
  if (!config.headers.Authorization) {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
};

authApiClient.interceptors.request.use(attachGatewayAndBearer);
stagingApiClient.interceptors.request.use(attachGatewayAndBearer);
export const authPostRequest = async <T = any>(endpoint: string, payload?: any): Promise<T> => {
  const res = await authApiClient.post<T>(endpoint, payload);
  return res.data;
};

export const authGetRequest = async <T = any>(
  endpoint: string,
  params?: any,
  config?: { signal?: AbortSignal; bearer?: string },
): Promise<T> => {
  const headers: Record<string, string> = {};
  const token = config?.bearer?.trim() || getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await authApiClient.get<T>(endpoint, {
    params,
    signal: config?.signal,
    headers,
  });
  return res.data;
};

function isAuthMeUserRecord(record: Record<string, unknown>): boolean {
  return (
    record.id != null ||
    record.email != null ||
    record.full_name != null ||
    record.preferred_username != null ||
    record.name != null ||
    record.sub != null
  );
}

function normalizeAuthMeUser(record: Record<string, unknown>): Record<string, unknown> {
  const normalized = { ...record };
  if (normalized.id == null && normalized.sub != null) {
    normalized.id = normalized.sub;
  }
  if (normalized.email == null && normalized.preferred_username != null) {
    normalized.email = normalized.preferred_username;
  }
  if (normalized.full_name == null && normalized.name != null) {
    normalized.full_name = normalized.name;
  }
  return normalized;
}

/**
 * Unwraps gateway session profile payloads: either { data: user } or nested { data: { data: user } }.
 * Keeps localStorage and sidebar/navbar working when the backend nests the profile.
 */
export function unwrapAuthMeUser(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;
  const inner = root.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const layer = inner as Record<string, unknown>;
    const nested = layer.data;
    const n = nested && typeof nested === "object" && !Array.isArray(nested) ? (nested as Record<string, unknown>) : null;
    if (n && isAuthMeUserRecord(n)) {
      return normalizeAuthMeUser(n);
    }
    if (isAuthMeUserRecord(layer)) {
      return normalizeAuthMeUser(layer);
    }
  }
  if (isAuthMeUserRecord(root)) {
    return normalizeAuthMeUser(root);
  }
  return null;
}

export interface AuthClientContext {
  authUser: Record<string, any>;
  roles: string[];
  isViewer: boolean;
  userId: string;
  creatorId: string;
  effectiveClientId: string;
}

export function getAuthClientContext(): AuthClientContext {
  let authUser: Record<string, any> = {};
  try {
    authUser = JSON.parse(localStorage.getItem("auth_user") ?? "{}");
  } catch {
    authUser = {};
  }

  const roles = Array.isArray(authUser?.roles)
    ? authUser.roles.map((r: unknown) => String(r))
    : [];
  const isViewer = roles.some((r) => r.toUpperCase() === "VIEWER");
  const userId = String(
    authUser?.id ??
      localStorage.getItem("auth_user_id") ??
      localStorage.getItem("clientId") ??
      ""
  ).trim();
  const creatorId = String(authUser?.creator_id ?? authUser?.creatorId ?? "").trim();
  const effectiveClientId = isViewer && creatorId ? creatorId : userId;

  return { authUser, roles, isViewer, userId, creatorId, effectiveClientId };
}

export function getEffectiveClientId(): string {
  return getAuthClientContext().effectiveClientId;
}

/** GET request to gateway (REACT_APP_API_GATEWAY_URL), e.g. /location/search */
export const stagingGetRequest = async <T = any>(endpoint: string, params?: any): Promise<T> => {
  const res = await stagingApiClient.get<T>(endpoint, { params });
  return res.data;
};

export const authPutRequest = async <T = any>(
  endpoint: string,
  payload?: any,
  params?: any
): Promise<T> => {
  const res = await authApiClient.put<T>(endpoint, payload, { params });
  return res.data;
};

export const authDeleteRequest = async <T = any>(endpoint: string, params?: any): Promise<T> => {
  const res = await authApiClient.delete<T>(endpoint, { params });
  return res.data;
};
